"use client";

import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area";

import { cn } from "@/lib/utils";

type ScrollAreaType = "auto" | "always" | "hover" | "scroll";

const scrollbarVisibility: Record<ScrollAreaType, string> = {
  always: "",
  auto: "",
  hover:
    "opacity-0 data-hovering:opacity-100 data-scrolling:opacity-100 transition-opacity duration-150",
  scroll:
    "opacity-0 data-scrolling:opacity-100 transition-opacity duration-150",
};

function ScrollArea({
  className,
  children,
  type = "auto",
  orientation = "vertical",
  ...props
}: ScrollAreaPrimitive.Root.Props & {
  type?: ScrollAreaType;
  /**
   * Which scrollbar(s) to render. Defaults to `vertical`; use `horizontal`
   * for a single row that overflows sideways (e.g. a chip strip), or `both`
   * for a 2-axis area.
   */
  orientation?: "vertical" | "horizontal" | "both";
}) {
  return (
    <ScrollAreaPrimitive.Root
      className={cn("relative", className)}
      data-slot="scroll-area"
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        className="size-full rounded-[inherit] outline-none transition-[color,box-shadow] focus-visible:outline-1 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        data-slot="scroll-area-viewport"
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {(orientation === "both"
        ? (["vertical", "horizontal"] as const)
        : [orientation]
      ).map((o) => (
        <ScrollBar
          className={scrollbarVisibility[type]}
          key={o}
          orientation={o}
        />
      ))}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: ScrollAreaPrimitive.Scrollbar.Props) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      className={cn(
        "flex touch-none select-none p-px transition-colors data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:border-t data-[orientation=horizontal]:border-t-transparent data-[orientation=vertical]:border-l data-[orientation=vertical]:border-l-transparent",
        className
      )}
      data-orientation={orientation}
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        className="relative flex-1 rounded-full bg-border"
        data-slot="scroll-area-thumb"
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
}

export { ScrollArea, ScrollBar };
